<script setup lang="ts">
    import type { AiMessage } from '~/stores/aiAssistantTypes';
    import { SPECIAL_SELECTION } from '~/utils/aiEntityMeta';
    import { MEILISEARCH_MODEL } from '~/app/common/enums/MeilisearchIndexType';
    import type { ContactsIndexSchema } from '~/app/common/schemas/ContactsIndexSchema';
    import type { ProductsIndexSchema } from '~/app/common/schemas/ProductsIndexSchema';
    import type { WorksitesIndexSchema } from '~/app/common/schemas/WorksitesIndexSchema';
    import { useSearchDropdown } from '~/composables/useSearchDropdown';

    type DisambigKind = 'contact' | 'product' | 'worksite' | null;

    const props = defineProps<{
        message: AiMessage;
    }>();

    const emit = defineEmits<{
        (e: 'option-selected', value: string, metadata?: Record<string, any>): void;
        (e: 'open-inline-form', value: string): void;
    }>();

    const selectedRadio = ref<string | null>(
        props.message.optionSelected && props.message.selectedLabel
            ? props.message.options?.find((o) => o.label === props.message.selectedLabel)?.value ?? null
            : null
    );

    // Detect what the agent is asking about so we can hit the right Meilisearch index when
    // the user wants to search beyond the 5 options the agent surfaced.
    const detectKind = (question: string): DisambigKind => {
        const q = question.toLowerCase();
        if (q.includes('contact')) return 'contact';
        if (q.includes('produit')) return 'product';
        if (q.includes('chantier')) return 'worksite';
        return null;
    };

    const kind = computed<DisambigKind>(() => detectKind(props.message.content));

    type Hit = ContactsIndexSchema | ProductsIndexSchema | WorksitesIndexSchema;
    const searchTerm = ref('');
    const searchActive = computed(() => searchTerm.value.trim().length > 0);

    // One dropdown per kind, instantiated once at setup so the inner refs are stable
    // and onScopeDispose correctly bound to this component (instead of a per-render
    // computed scope, which would leak timers and lose reactivity).
    const contactDropdown = useSearchDropdown<ContactsIndexSchema>({ model: MEILISEARCH_MODEL.CONTACTS, debounceMs: 200 });
    const productDropdown = useSearchDropdown<ProductsIndexSchema>({ model: MEILISEARCH_MODEL.PRODUCTS, debounceMs: 200 });
    const worksiteDropdown = useSearchDropdown<WorksitesIndexSchema>({ model: MEILISEARCH_MODEL.WORKSITES, debounceMs: 200 });

    const activeDropdown = computed(() => {
        if (kind.value === 'contact') return contactDropdown;
        if (kind.value === 'product') return productDropdown;
        if (kind.value === 'worksite') return worksiteDropdown;
        return null;
    });

    const searchEnabled = computed(() => activeDropdown.value !== null);
    const searchResults = computed<Hit[]>(() => activeDropdown.value?.results.value ?? []);

    const triggerSearch = () => {
        activeDropdown.value?.search(searchTerm.value);
    };

    const hitLabel = (hit: Hit): string => {
        if (kind.value === 'contact') return (hit as ContactsIndexSchema).display_name ?? '';
        if (kind.value === 'worksite') return (hit as WorksitesIndexSchema).title ?? '';
        if (kind.value === 'product') return (hit as ProductsIndexSchema).title ?? '';
        return '';
    };

    const hitMeta = (hit: Hit): string | null => {
        if (kind.value === 'contact') return (hit as ContactsIndexSchema).email ?? null;
        if (kind.value === 'worksite') return (hit as WorksitesIndexSchema).address_display ?? null;
        return null;
    };

    const selectOption = (value: string) => {
        if (props.message.optionSelected) return;
        if (value === SPECIAL_SELECTION.FreeLine || value === SPECIAL_SELECTION.CreateNew) {
            emit('open-inline-form', value);
            return;
        }
        selectedRadio.value = value;
        emit('option-selected', value);
    };

    const selectFromSearch = (hit: Hit) => {
        if (props.message.optionSelected) return;
        selectedRadio.value = hit.uuid;
        emit('option-selected', hit.uuid);
    };

    const isSpecial = (value: string): boolean =>
        value === SPECIAL_SELECTION.FreeLine || value === SPECIAL_SELECTION.CreateNew;
</script>

<template>
    <!-- Submitted free line summary -->
    <div
        v-if="message.optionSelected && message.freeLineData"
        class="p-[10px] border border-gray-200 rounded-[6px] bg-gray-50 opacity-60"
    >
        <p class="text-[12px] text-gray-600">
            {{ $t('ai.chat.free_line_summary', { title: message.freeLineData.title }) }} — {{ message.freeLineData.price }}€ x {{ message.freeLineData.quantity }} {{ message.freeLineData.unit }}, {{ $t('form.labels.vat') }} {{ message.freeLineData.vatRate }}%
        </p>
    </div>

    <div
        v-else
        class="flex flex-col gap-[10px]"
    >
        <!-- Meilisearch input — only when we know the entity kind, hidden once an option is locked in -->
        <div
            v-if="searchEnabled && !message.optionSelected"
            class="relative"
        >
            <i class="fa-solid fa-magnifying-glass absolute left-[10px] top-1/2 -translate-y-1/2 text-[11px] text-gray-400 pointer-events-none" />
            <input
                v-model="searchTerm"
                type="text"
                :placeholder="$t('ai.chat.search_or_filter')"
                class="min-h-[32px] box-border leading-none w-full text-black transition-all placeholder-gray-500 font-thin rounded-md bg-white outline-none shadow-[0px_1px_2px_0px_rgba(0,0,0,0.1)] border border-gray-200 focus:border-black p-2.5 pl-[28px] text-sm"
                @input="triggerSearch"
            />
        </div>

        <!-- Meilisearch results (replace radios while user is typing) -->
        <div
            v-if="searchActive && searchResults.length"
            class="flex flex-col gap-[6px]"
        >
            <button
                v-for="hit in searchResults"
                :key="hit.uuid"
                type="button"
                class="flex flex-col items-start gap-[2px] w-full text-left px-[10px] py-[8px] rounded-[8px] border border-transparent hover:bg-gray-50 transition-colors cursor-pointer"
                :class="selectedRadio === hit.uuid ? 'bg-[#FEF8EA] border-[#E8B102] shadow-[0_0_0_3px_rgba(255,196,0,0.18)]' : ''"
                @click="selectFromSearch(hit)"
            >
                <span class="font-inter text-[12px] text-gray-900 truncate w-full">{{ hitLabel(hit) }}</span>
                <span
                    v-if="hitMeta(hit)"
                    class="font-inter text-[10px] text-gray-400 truncate w-full"
                >
                    {{ hitMeta(hit) }}
                </span>
            </button>
        </div>

        <!-- No matches found -->
        <div
            v-else-if="searchActive && !searchResults.length"
            class="px-[10px] py-[12px] text-center font-inter text-[11px] text-gray-400 italic"
        >
            {{ $t('ai.chat.no_search_results') }}
        </div>

        <!-- Original AI options (shown when search is empty) -->
        <div
            v-else
            class="flex flex-col gap-[6px]"
        >
            <button
                v-for="option in message.options"
                :key="option.value"
                :disabled="message.optionSelected"
                type="button"
                class="flex gap-[10px] items-center w-full text-left rounded-[8px] px-[10px] py-[8px] transition-all cursor-pointer border"
                :class="[
                    isSpecial(option.value) && selectedRadio !== option.value
                        ? 'border-dashed border-[#D4D4D8] bg-transparent hover:bg-[#FEFCF6]'
                        : '',
                    !isSpecial(option.value) && selectedRadio !== option.value
                        ? 'border-transparent bg-transparent hover:bg-gray-50'
                        : '',
                    selectedRadio === option.value
                        ? 'bg-[#FEF8EA] border-[#E8B102] shadow-[0_0_0_3px_rgba(255,196,0,0.18)]'
                        : '',
                    message.optionSelected && selectedRadio !== option.value ? 'opacity-40 cursor-not-allowed hover:bg-transparent' : '',
                ]"
                @click="selectOption(option.value)"
            >
                <span
                    class="shrink-0 w-[16px] h-[16px] rounded-full border flex items-center justify-center transition-colors"
                    :class="selectedRadio === option.value ? 'border-[#E8B102] bg-branding-brand-yellow' : 'border-gray-300 bg-white'"
                >
                    <span
                        v-if="selectedRadio === option.value"
                        class="w-[6px] h-[6px] rounded-full bg-black"
                    />
                </span>
                <span class="flex-1 min-w-0">
                    <span
                        class="block font-inter text-[12px] leading-[1.3] truncate"
                        :class="[
                            selectedRadio === option.value ? 'font-medium text-gray-900' : '',
                            isSpecial(option.value) && selectedRadio !== option.value ? 'italic text-gray-500' : 'text-gray-900',
                        ]"
                    >
                        {{ option.label }}
                    </span>
                </span>
                <i
                    v-if="isSpecial(option.value)"
                    class="fa-solid fa-plus text-[11px] text-gray-400 flex-shrink-0"
                />
            </button>
        </div>
    </div>
</template>
